import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import sanitizeHtml from 'sanitize-html';

import { useLocalStorage } from '../../common/hooks/useLocalStorage';
import { Channels } from '@bandido/shared/constants';

interface DraftContextProps {
  draft: DraftCase;
  setDraft: (value: DraftCase | ((prev: DraftCase) => DraftCase)) => void;
  removeDraft: (channel: Channels) => void;
  draftPreview: (channel: Channels.EMAIL | Channels.NOTES) => string;
}

const DraftContext = createContext<DraftContextProps | undefined>(undefined);

interface DraftProviderProps {
  caseId: string;
  children: ReactNode;
}

interface DraftCase {
  [Channels.EMAIL]?: string;
  [Channels.NOTES]?: string;
}

export const DraftProvider = ({ caseId, children }: DraftProviderProps) => {
  const [draft, setDraft, removeDraft] = useLocalStorage<DraftCase>(caseId, {});

  const draftPreview = useCallback(
    (channel: Channels.EMAIL | Channels.NOTES) => {
      if (!draft) return '';

      try {
        const plainText = sanitizeHtml(draft[channel] ?? '', {
          allowedTags: [],
          allowedAttributes: {},
        })
          .replace(/\s+/g, ' ')
          .trim();

        return plainText;
      } catch (error) {
        console.warn('Error parsing draft preview:', error);
        return '';
      }
    },
    [draft],
  );

  const value = useMemo(
    () => ({
      draft,
      setDraft,
      removeDraft,
      draftPreview,
    }),
    [draft, setDraft, removeDraft, draftPreview],
  );

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
};

export const useDraft = (): DraftContextProps => {
  const context = useContext(DraftContext);
  if (!context) {
    throw new Error('useDraft must be used within a DraftProvider');
  }
  return context;
};
